import { Provider as AlertProvider } from "@blaumaus/react-alert";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Suspense, lazy, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { SkeletonTheme } from "react-loading-skeleton";
import { Provider, useSelector } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import {
  AllCommunityModule,
  ModuleRegistry,
  provideGlobalGridOptions,
} from "ag-grid-community";
import * as serviceWorker from "./serviceWorkerRegistration";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "react-datepicker/dist/react-datepicker.css";
import "react-loading-skeleton/dist/skeleton.css";
import "./i18n/i18n"; // i18n side-effect initialization — must be before component imports
import i18n from "./i18n/i18n";
import { pdfjs } from "react-pdf";
import LoadingAnimation from "./components/Animations/LoadingAnimation";
import { clientEnv } from "src/config/clientEnv";
import { options } from "./features/alert/AlertTemplate";
import useAuth from "./features/auth/hooks/useAuth";
import { useProactiveTokenRefresh } from "./features/auth/hooks/useProactiveTokenRefresh";
import hasGroupPermissions from "./features/auth/utils/hasGroupPermissions";
import { selectLocale } from "./features/i18n/localeSlice";
import { isRtl } from "./i18n/constants";
import { UrlPaths } from "./features/site/types/routes.types";
import ProtectedEmployeeBulkCreate from "./features/employees/routes/ProtectedEmployeeBulkCreate";
import ProtectedEmployeeBulkRandomCreate from "./features/employees/routes/ProtectedEmployeeBulkRandomCreate";
import { NON_EMPLOYEES_GROUP } from "./features/groups/groups";
import OnboardingWrapper from "./features/onboarding/routes/OnboardingWrapper";
import lazyRetry from "./features/utils/lazyRetry";
import "./index.css";
import { persistor, store } from "./store";
import PrivacyPolicyPage from "./features/legal/routes/PrivacyPolicyPage";
import CookiesPolicyPage from "./features/legal/routes/CookiesPolicyPage";
import TermsOfUsePage from "./features/legal/routes/TermsOfUsePage";
import EndUserLicensePage from "./features/legal/routes/EndUserLicensePage";
import LegalAgreementsPage from "./features/legal/routes/LegalAgreementsPage";
import SupportPage from "./features/support/components/SupportPage";
import MaintenancePage from "./features/maintenance/MaintenancePage";
import { UpcomingMaintenanceBanner } from "./features/maintenance/UpcomingMaintenanceBanner";
import ProtectedDocsPage from "./features/docs/routes/protected-docs-page";

ModuleRegistry.registerModules([AllCommunityModule]);
provideGlobalGridOptions({ theme: "legacy" });

const AssessmentsListPage = lazy(() =>
  lazyRetry(() => import("./features/assessments/routes/AssessmentsListPage"))
);
const SittingCompletedPage = lazy(() =>
  lazyRetry(() => import("./features/assessments/routes/SittingCompletedPage"))
);
const SittingTakeV2 = lazy(() =>
  lazyRetry(() => import("./features/assessments/routes/SittingTakeV2Page"))
);
const DocumentApprovalForm = lazy(() =>
  lazyRetry(
    () =>
      import(
        "./features/e-signatures/components/Documents/DocumentApprovalForm"
      )
  )
);
const DocumentSignRoute = lazy(() =>
  lazyRetry(() => import("./features/e-signatures/routes/DocumentSignRoute"))
);
const ESignaturesDashboard = lazy(() =>
  lazyRetry(() => import("./features/e-signatures/routes/ESignaturesDashboard"))
);
const ProtectedESignaturesWithSidebarRoute = lazy(() =>
  lazyRetry(
    () =>
      import(
        "./features/e-signatures/routes/ProtectedESignaturesWithSidebarRoute"
      )
  )
);
const UpdateEmployeeTypesForm = lazy(() =>
  lazyRetry(
    () => import("./features/employeeTypes/components/UpdateEmployeeTypesForm")
  )
);
const LearningPathsListPage = lazy(() =>
  lazyRetry(
    () => import("./features/learningPaths/routes/LearningPathsListPage")
  )
);
const LearningPathsTakeRoute = lazy(() =>
  lazyRetry(
    () => import("./features/learningPaths/routes/LearningPathsTakeRoute")
  )
);
const MemoCreateEditPage = lazy(() =>
  lazyRetry(
    () => import("./features/memos/components/Steps/MemoCreateEditPage")
  )
);
const OnboardingAdminAccount = lazy(() =>
  lazyRetry(() => import("./features/onboarding/routes/OnboardingAdminAccount"))
);
const OnboardingDepartments = lazy(() =>
  lazyRetry(() => import("./features/onboarding/routes/OnboardingDepartments"))
);
const OnboardingEmployeeTypes = lazy(() =>
  lazyRetry(
    () => import("./features/onboarding/routes/OnboardingEmployeeTypes")
  )
);
const PolicyCreateEditPage = lazy(() =>
  lazyRetry(
    () => import("./features/policies/components/Steps/PolicyCreateEditPage")
  )
);
const EmployeeWeeklySchedule = lazy(() =>
  lazyRetry(() => import("./features/schedules/pages/employee-weekly-schedule"))
);
const LaborBudgetWeeklyPage = lazy(() =>
  lazyRetry(() => import("./features/schedules/pages/labor-budget"))
);
const SchedulesSupervisorDashboard = lazy(() =>
  lazyRetry(() => import("./features/schedules/pages/supervisor-dashboard"))
);
const ShiftConflicts = lazy(() =>
  lazyRetry(() => import("./features/schedules/pages/shift-conflicts"))
);
const ShiftTransferDetailPage = lazy(() =>
  lazyRetry(
    () =>
      import(
        "./features/schedules/pages/shift-transfers/ShiftTransferDetailPage"
      )
  )
);
const ShiftTransfers = lazy(() =>
  lazyRetry(() => import("./features/schedules/pages/shift-transfers"))
);
const SchedulesLayout = lazy(() =>
  lazyRetry(() => import("./features/schedules/components/schedules-layout"))
);
const DepartmentCalendarPage = lazy(() =>
  lazyRetry(() => import("./features/schedules/pages/department-calendar"))
);
const TimeOffRequestsOnboardingStep1 = lazy(() =>
  lazyRetry(
    () =>
      import(
        "./features/timeOffRequests/components/TimeOffRequestsOnboardingStep1"
      )
  )
);
const TimeOffRequestsOnboardingStep2 = lazy(() =>
  lazyRetry(
    () =>
      import(
        "./features/timeOffRequests/components/TimeOffRequestsOnboardingStep2"
      )
  )
);
const TimeOffRequestsOnboardingStep3 = lazy(() =>
  lazyRetry(
    () =>
      import(
        "./features/timeOffRequests/components/TimeOffRequestsOnboardingStep3"
      )
  )
);
const TimeOffRequestsOnboardingStep4 = lazy(() =>
  lazyRetry(
    () =>
      import(
        "./features/timeOffRequests/components/TimeOffRequestsOnboardingStep4"
      )
  )
);
const TimeOffRequestsOnboardingStep5 = lazy(() =>
  lazyRetry(
    () =>
      import(
        "./features/timeOffRequests/components/TimeOffRequestsOnboardingStep5"
      )
  )
);
const TimeOffRequestsOnboardingStep6 = lazy(() =>
  lazyRetry(
    () =>
      import(
        "./features/timeOffRequests/components/TimeOffRequestsOnboardingStep6"
      )
  )
);
const TimeOffRequestDashboard = lazy(() =>
  lazyRetry(
    () =>
      import("./features/timeOffRequests/components/TimeOffRequestDashboard")
  )
);
const BlackoutPeriodsListPage = lazy(() =>
  lazyRetry(
    () =>
      import("./features/timeOffRequests/components/BlackoutPeriodsListPage")
  )
);
const ProtectedTimeOffRequest = lazy(() =>
  lazyRetry(
    () => import("./features/timeOffRequests/routes/ProtectedTimeOffRequest")
  )
);
const VideoCreateEditPage = lazy(() =>
  lazyRetry(() => import("./features/videos/routes/VideoCreateEditPage"))
);
const AgendaEventCreateEditPage = lazy(() =>
  lazyRetry(
    () =>
      import("./features/calendars/components/Steps/AgendaEventCreateEditPage")
  )
);
const ProtectedAgendaRoute = lazy(() =>
  lazyRetry(() => import("./features/calendars/routes/ProtectedAgendaRoute"))
);
const LearningPathForm = lazy(() =>
  lazyRetry(() => import("./features/learningPaths/routes/LearningPathForm"))
);
const LoginPage = lazy(() =>
  lazyRetry(() => import("./features/auth/routes/LoginPage"))
);
const PasswordResetRequestPage = lazy(() =>
  lazyRetry(() => import("./features/auth/routes/PasswordResetRequestPage"))
);
const ResetPasswordPage = lazy(() =>
  lazyRetry(() => import("./features/auth/routes/ResetPasswordPage"))
);
const UsernameRequestPage = lazy(() =>
  lazyRetry(() => import("./features/auth/routes/UsernameRequestPage"))
);
const BillingSetupFlow = lazy(() =>
  lazyRetry(() => import("./features/settings/BillingSetupFlow"))
);
const BillingDetailsPage = lazy(() =>
  lazyRetry(() => import("./features/settings/BillingDetailsPage"))
);
const SubscriptionAndTerms = lazy(() =>
  lazyRetry(() => import("./features/settings/SubscriptionAndTerms"))
);
const PaymentMethod = lazy(() =>
  lazyRetry(() => import("./features/settings/PaymentMethod"))
);
const PoliciesStatsPage = lazy(() =>
  lazyRetry(() => import("./features/policies/components/PoliciesStatsPage"))
);
const ProtectedAssessmentsRoute = lazy(() =>
  lazyRetry(
    () => import("./features/assessments/routes/ProtectedAssessmentsRoute")
  )
);
const DocumentDetailRoute = lazy(() =>
  lazyRetry(() => import("./features/e-signatures/routes/DocumentDetailRoute"))
);
const LearningPathDetailPage = lazy(() =>
  lazyRetry(
    () => import("./features/learningPaths/routes/LearningPathDetailPage")
  )
);
const EmployeeLearningPathDetailPage = lazy(() =>
  lazyRetry(
    () =>
      import("./features/learningPaths/routes/EmployeeLearningPathDetailPage")
  )
);
const AssessmentCreateEditPage = lazy(() =>
  lazyRetry(() => import("./features/assessments/routes/AssessmentForm"))
);
const AssessmentDetailPage = lazy(() =>
  lazyRetry(() => import("./features/assessments/routes/AssessmentDetailPage"))
);
const EmployeeSittingRoute = lazy(() =>
  lazyRetry(() => import("./features/assessments/routes/EmployeeSittingRoute"))
);
const ProtectedESignaturesRoute = lazy(() =>
  lazyRetry(
    () => import("./features/e-signatures/routes/ProtectedESignaturesRoute")
  )
);
const TemplateForm = lazy(() =>
  lazyRetry(
    () =>
      import(
        "./features/e-signatures/components/TemplateComponents/TemplateForm"
      )
  )
);
const DocumentAssignForm = lazy(() =>
  lazyRetry(
    () =>
      import("./features/e-signatures/components/Documents/DocumentAssignForm")
  )
);
const LogoutPage = lazy(() =>
  lazyRetry(() => import("./features/auth/routes/LogoutPage"))
);
const OrganizationForm = lazy(() =>
  lazyRetry(() => import("./components/Forms/OrganizationForm"))
);
const LandingPage = lazy(() =>
  lazyRetry(() => import("./features/landing/routes/LandingPage"))
);
const ProtectedDashboard = lazy(() =>
  lazyRetry(() => import("./features/dashboard/routes/ProtectedDashboardPage"))
);
const ProtectedDiscussions = lazy(() =>
  lazyRetry(() => import("./features/discussions/routes/ProtectedDiscussions"))
);
const Discussion = lazy(() =>
  lazyRetry(() => import("./features/discussions/components/Discussion"))
);
const ProtectedOrganization = lazy(() =>
  lazyRetry(
    () => import("./features/organization/routes/ProtectedOrganization")
  )
);
const ProtectedUpdateOrganization = lazy(() =>
  lazyRetry(
    () => import("./features/organization/routes/ProtectedUpdateOrganization")
  )
);
const ProtectedDepartments = lazy(() =>
  lazyRetry(() => import("./features/departments/routes/ProtectedDepartments"))
);
const ProtectedDepartmentsUpdate = lazy(() =>
  lazyRetry(
    () => import("./features/departments/routes/ProtectedDepartmentsUpdate")
  )
);
const ProtectedRoles = lazy(() =>
  lazyRetry(() => import("./features/employeeTypes/routes/ProtectedRoles"))
);
const ProtectedEmployeesRoute = lazy(() =>
  lazyRetry(() => import("./features/employees/routes/ProtectedEmployeesRoute"))
);
const ProtectedEmployeeDetail = lazy(() =>
  lazyRetry(() => import("./features/employees/routes/ProtectedEmployeeDetail"))
);
const ProtectedMemosRoute = lazy(() =>
  lazyRetry(() => import("./features/memos/routes/ProtectedMemosRoute"))
);
const MemoStatsPage = lazy(() =>
  lazyRetry(() => import("./features/memos/components/MemoStatsPage"))
);
const MemoDetailPage = lazy(() =>
  lazyRetry(() => import("./features/memos/components/MemoDetailPage"))
);
const ProtectedVideosRoute = lazy(() =>
  lazyRetry(() => import("./features/videos/routes/ProtectedVideosRoute"))
);
const SettingsPage = lazy(() =>
  lazyRetry(() => import("./features/settings/routes/ProtectedSettingsRoute"))
);
const VideosListPage = lazy(() =>
  lazyRetry(() => import("./features/videos/routes/VideosListPage"))
);
const VideosDetailPage = lazy(() =>
  lazyRetry(() => import("./features/videos/components/VideosDetailPage"))
);
const VideosStatsPage = lazy(() =>
  lazyRetry(() => import("./features/videos/components/VideosStatsPage"))
);
const NotFound = lazy(() =>
  lazyRetry(() => import("./features/errors/routes/NotFound"))
);
const ProtectedRotationsLayoutRoute = lazy(() =>
  lazyRetry(
    () => import("./features/rotations/routes/ProtectedRotationsLayoutRoute")
  )
);
const RotationsIndexRedirect = lazy(() =>
  lazyRetry(() => import("./features/rotations/routes/RotationsIndexRedirect"))
);
const ProtectedRotationSettingsRoute = lazy(() =>
  lazyRetry(
    () => import("./features/rotations/routes/ProtectedRotationSettingsRoute")
  )
);
const ProtectedRotationBoardRoute = lazy(() =>
  lazyRetry(
    () => import("./features/rotations/routes/ProtectedRotationBoardRoute")
  )
);
const ProtectedRotationSheetRoute = lazy(() =>
  lazyRetry(
    () => import("./features/rotations/routes/ProtectedRotationSheetRoute")
  )
);
const ProtectedMyRotationShiftRoute = lazy(() =>
  lazyRetry(
    () => import("./features/rotations/routes/ProtectedMyRotationShiftRoute")
  )
);
const RotationDisplayRoute = lazy(() =>
  lazyRetry(() => import("./features/rotations/routes/RotationDisplayRoute"))
);

const ProtectedLearningPaths = lazy(() =>
  lazyRetry(
    () => import("./features/learningPaths/routes/ProtectedLearningPaths")
  )
);

const ProtectedPoliciesRoute = lazy(() =>
  lazyRetry(() => import("./features/policies/routes/ProtectedPoliciesRoute"))
);

const PoliciesDetailPage = lazy(() =>
  lazyRetry(() => import("./features/policies/components/PoliciesDetailPage"))
);

Sentry.init({
  dsn: "https://682e0f1de6554c62a2734b02e5cf94fe@o1380729.ingest.sentry.io/4503903422119936",
  integrations: [Sentry.browserTracingIntegration()],
  environment: clientEnv.mode,
  enabled: clientEnv.isProduction,
  tracesSampleRate: 0.2,
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: 0.2,
  // Leaving dataCollection unset collects cookies, request/response bodies and
  // user IPs. Sessions are cookie-authenticated and API payloads carry employee
  // data, so every category is kept off or scrubbed here.
  dataCollection: {
    userInfo: false,
    cookies: false,
    httpHeaders: {
      request: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
      response: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
    },
    httpBodies: [],
    urlQueryParams: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
    genAI: { inputs: false, outputs: false },
    databaseQueryData: false,
    queues: false,
    graphQL: { document: false, variables: false },
  },
});

// The worker is a separate JavaScript scope, so polyfills installed on the main
// thread do not reach it. It must load the same legacy build aliased in
// vite.config.ts, or PDFs fail inside the worker on pre-Chrome-126 browsers.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/legacy/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const AppRoutes = () => {
  const { employee } = useAuth();
  const currentLocale = useSelector(selectLocale);

  // Sync i18n with the persisted Redux locale on rehydration / mount
  useEffect(() => {
    if (currentLocale && i18n.language !== currentLocale) {
      i18n.changeLanguage(currentLocale);
      document.documentElement.lang = currentLocale;
      document.documentElement.dir = isRtl(currentLocale) ? "rtl" : "ltr";
    }
  }, [currentLocale]);

  // Proactively refresh tokens before they expire
  useProactiveTokenRefresh();

  return (
    <BrowserRouter basename={"/"}>
      <UpcomingMaintenanceBanner>
        <Suspense
          fallback={<LoadingAnimation containerClassName={"h-screen centered"} />}
        >
          <Routes>
          <Route path={"maintenance"} element={<MaintenancePage />} />
          <Route path={"support"} element={<SupportPage />} />
          <Route path={"docs/*"} element={<ProtectedDocsPage />} />
          <Route path={"legal-agreements"} element={<LegalAgreementsPage />} />
          <Route path={"privacy-policy"} element={<PrivacyPolicyPage />} />
          <Route path={"terms-of-use"} element={<TermsOfUsePage />} />
          <Route path={"cookies-policy"} element={<CookiesPolicyPage />} />
          <Route path={"end-user-license"} element={<EndUserLicensePage />} />
          <Route path={"login"} element={<LoginPage />} />
          {/* The marquee: no auth guard, no shell; the token in the path is the gate. */}
          <Route
            path={`${UrlPaths.RotationDisplay}/:token`}
            element={<RotationDisplayRoute />}
          />
          <Route path={"logout"} element={<LogoutPage />} />
          <Route path={"username-request"} element={<UsernameRequestPage />} />
          <Route
            path={"reset-password-request"}
            element={<PasswordResetRequestPage />}
          />
          <Route path={"reset-password"} element={<ResetPasswordPage />} />
          <Route path={"register"} element={<OnboardingWrapper />}>
            <Route path={"organization"} element={<OrganizationForm />} />
            <Route path={"roles"} element={<OnboardingEmployeeTypes />} />
            <Route path={"departments"} element={<OnboardingDepartments />} />
            <Route path={"admin"} element={<OnboardingAdminAccount />} />
          </Route>
          <Route
            path={"settings/time-off-requests/onboarding"}
            element={<OnboardingWrapper totalSteps={6} />}
          >
            <Route index element={<TimeOffRequestsOnboardingStep1 />} />
            <Route
              path={"form-step-2"}
              element={<TimeOffRequestsOnboardingStep2 />}
            />
            <Route
              path={"form-step-3"}
              element={<TimeOffRequestsOnboardingStep3 />}
            />
            <Route
              path={"form-step-4"}
              element={<TimeOffRequestsOnboardingStep4 />}
            />
            <Route
              path={"form-step-5"}
              element={<TimeOffRequestsOnboardingStep5 />}
            />
            <Route
              path={"form-step-6"}
              element={<TimeOffRequestsOnboardingStep6 />}
            />
          </Route>
          <Route
            path={"employees/bulk-create"}
            element={<ProtectedEmployeeBulkCreate />}
          />
          <Route
            path={"employees/bulk-create-random"}
            element={<ProtectedEmployeeBulkRandomCreate />}
          />
          <Route path={"billing"} element={<BillingSetupFlow />}>
            <Route path={"subscription"} element={<SubscriptionAndTerms />} />
            <Route path={"details"} element={<BillingDetailsPage />} />
            <Route path={"payment-method"} element={<PaymentMethod />} />
          </Route>
          <Route path={""} element={<LandingPage />} />
          <Route path={"dashboard"} element={<ProtectedDashboard />} />
          <Route
            path={"time-off-requests"}
            element={<ProtectedTimeOffRequest />}
          >
            <Route index element={<TimeOffRequestDashboard />} />
            <Route
              path={"blackout-periods"}
              element={<BlackoutPeriodsListPage />}
            />
          </Route>
          <Route path={"schedules"} element={<SchedulesLayout />}>
            <Route
              index
              element={
                hasGroupPermissions(employee, NON_EMPLOYEES_GROUP) ? (
                  <SchedulesSupervisorDashboard />
                ) : (
                  <EmployeeWeeklySchedule />
                )
              }
            />
            <Route path="me" element={<EmployeeWeeklySchedule />} />
            <Route
              path={"departments/:id"}
              element={<DepartmentCalendarPage />}
            />
            <Route path={"shift-transfers"} element={<ShiftTransfers />} />
            <Route
              path="shift-transfers/:id"
              element={<ShiftTransferDetailPage />}
            />
            <Route path={"shift-conflicts"} element={<ShiftConflicts />} />
            <Route path={"labor-budget"} element={<LaborBudgetWeeklyPage />} />
          </Route>
          <Route
            path={UrlPaths.Rotations}
            element={<ProtectedRotationsLayoutRoute />}
          >
            <Route index element={<RotationsIndexRedirect />} />
            <Route
              path={"settings"}
              element={<ProtectedRotationSettingsRoute />}
            />
            <Route
              path={"settings/groups/:groupId"}
              element={<ProtectedRotationSettingsRoute />}
            />
            <Route
              path={":groupId/board"}
              element={<ProtectedRotationBoardRoute />}
            />
            <Route
              path={"days/:dayId/sheet"}
              element={<ProtectedRotationSheetRoute />}
            />
            <Route path={"me"} element={<ProtectedMyRotationShiftRoute />} />
          </Route>
          <Route path={"discussions"} element={<ProtectedDiscussions />}>
            <Route index element={<Discussion />} />
            <Route path={":uuid"} element={<Discussion />} />
          </Route>
          <Route path={"memos"} element={<ProtectedMemosRoute />}>
            <Route
              index
              element={
                hasGroupPermissions(employee, NON_EMPLOYEES_GROUP) ? (
                  <MemoStatsPage />
                ) : (
                  <MemoDetailPage />
                )
              }
            />
            <Route path={":id"} element={<MemoDetailPage />} />
            <Route path={"form"} element={<MemoCreateEditPage />} />
            <Route path={"form/:id"} element={<MemoCreateEditPage />} />
          </Route>
          <Route path={"agendas"} element={<ProtectedAgendaRoute />}>
            <Route path={"form"} element={<AgendaEventCreateEditPage />} />
            <Route path={"form/:id"} element={<AgendaEventCreateEditPage />} />
          </Route>
          <Route path={"organization"} element={<ProtectedOrganization />}>
            <Route path={"update"} element={<ProtectedUpdateOrganization />} />
          </Route>
          <Route path={"departments"} element={<ProtectedDepartments />}>
            <Route path={"update"} element={<ProtectedDepartmentsUpdate />} />
          </Route>
          <Route path={"roles"} element={<ProtectedRoles />}>
            <Route path={"update"} element={<UpdateEmployeeTypesForm />} />
          </Route>
          <Route path={"employees"} element={<ProtectedEmployeesRoute />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path={":id"} element={<ProtectedEmployeeDetail />} />
          </Route>
          <Route path={"assessments"} element={<ProtectedAssessmentsRoute />}>
            <Route index element={<AssessmentsListPage />} />
            <Route path={":id"} element={<AssessmentDetailPage />} />
            <Route path={"form"} element={<AssessmentCreateEditPage />} />
            <Route path={`form/:id`} element={<AssessmentCreateEditPage />} />
            <Route path={"sittings/:id/take"} element={<SittingTakeV2 />} />
            <Route
              path={"sittings/:id/result"}
              element={<SittingCompletedPage />}
            />
            <Route path={`marking/:id`} element={<EmployeeSittingRoute />} />
          </Route>
          <Route path={"learning-paths"} element={<ProtectedLearningPaths />}>
            <Route index element={<LearningPathsListPage />} />
            <Route path={":id"} element={<LearningPathDetailPage />} />
            <Route path={"form"} element={<LearningPathForm />} />
            <Route path={`form/:id/`} element={<LearningPathForm />} />
            <Route
              path={`employee-path/:id/`}
              element={<EmployeeLearningPathDetailPage />}
            />
          </Route>
          <Route
            path={"employee-learning-paths/:id/take"}
            element={<LearningPathsTakeRoute />}
          />
          <Route path={"policies"} element={<ProtectedPoliciesRoute />}>
            <Route
              index
              element={
                hasGroupPermissions(employee, NON_EMPLOYEES_GROUP) ? (
                  <PoliciesStatsPage />
                ) : (
                  <PoliciesDetailPage />
                )
              }
            />
            <Route path={":id"} element={<PoliciesDetailPage />} />
            <Route path={"form"} element={<PolicyCreateEditPage />} />
            <Route path={`form/:id`} element={<PolicyCreateEditPage />} />
          </Route>
          <Route path={"e-signatures"} element={<ProtectedESignaturesRoute />}>
            <Route index element={<ESignaturesDashboard />} />
          </Route>
          <Route
            path={"e-signatures"}
            element={<ProtectedESignaturesWithSidebarRoute />}
          >
            <Route path={"templates/create"} element={<TemplateForm />} />
            <Route path={`templates/:id/edit`} element={<TemplateForm />} />
            <Route path={`documents/:id`} element={<DocumentDetailRoute />} />
            <Route path="documents/:id/sign" element={<DocumentSignRoute />} />
            <Route
              path={"documents/:id/approve"}
              element={<DocumentApprovalForm />}
            />
            <Route
              path={`documents/:id/assign`}
              element={<DocumentAssignForm />}
            />
          </Route>
          <Route path={"videos"} element={<ProtectedVideosRoute />}>
            <Route index element={<VideosListPage />} />
            <Route path={":id"} element={<VideosDetailPage />} />
            <Route path={"form"} element={<VideoCreateEditPage />} />
            <Route path={"form/:id"} element={<VideoCreateEditPage />} />
            <Route path={"stats"} element={<VideosStatsPage />} />
          </Route>
          <Route path={"settings"} element={<SettingsPage />} />
          <Route path={"*"} element={<NotFound />} />
        </Routes>
        </Suspense>
      </UpcomingMaintenanceBanner>
    </BrowserRouter>
  );
};

export const clearpathPhone = clientEnv.clearpathPhone;

if (!clearpathPhone) {
  console.warn("REACT_APP_CLEARPATH_PHONE is not set, using default value.");
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});
const container = document.getElementById("root");
const root = createRoot(container!);

root.render(
  <Provider store={store}>
    <AlertProvider {...options}>
      <PersistGate loading={<LoadingAnimation />} persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          <SkeletonTheme
            baseColor="#131316"
            highlightColor="#232227"
            borderRadius={5}
          >
            <AppRoutes />
          </SkeletonTheme>
        </QueryClientProvider>
      </PersistGate>
    </AlertProvider>
  </Provider>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
